/** Sensor configuration editor backed by the dashboard unit contract. */
import { Box, Button, Skeleton, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { Form, Formik, setIn } from 'formik';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { patchSiteSensorConfig } from '@sophocles/api/odlApp';
import { dispatchSensorOperation } from '@sophocles/api/siteCommands';
import FormErrorAlert from '@sophocles/components/FormErrorAlert';
import { getAuthUserSelector } from '@sophocles/features/auth/authSelectors';
import { sensorConfigRequiresCommandLease } from '@sophocles/features/entities/site';
import { addToast } from '@sophocles/features/toast';
import useSiteCommandWrite from '@sophocles/hooks/useSiteCommandWrite';
import { applyApiErrorToFormik } from '@sophocles/utils/apiErrors';
import { getUserMeasurementSystem } from '@sophocles/utils/formatUtils';
import {
  displayDeltaTemp,
  displayRh,
  displayTemp,
  inputToRawDeltaTemp,
  inputToRawRh,
  inputToRawTemp,
} from '@sophocles/utils/sensorConversions';
import { getSensorTypeName } from '@sophocles/utils/sensorTypes';
import ConfigurationBladeTabs from '@sophocles/views/sites/components/SiteDashboard/ConfigurationBladeTabs';
import DashboardBlade from '@sophocles/views/sites/components/SiteDashboard/DashboardBlade';
import {
  SENSOR_CONFIG_SCHEMA_VERSION,
  buildCommonInitialValue,
  buildSensorPatchKnownFields,
  buildSensorBladeTabs,
  buildSensorInitialValues,
  buildSensorValidationSchema,
  generateSensorConfigPatch,
  mapSensorPatchErrorPath,
} from '@sophocles/views/sites/components/SiteDashboard/sensorConfigForm';

const BLADE_WIDTH = { xs: '100%', sm: 640 };
const SENSOR_TYPE_COLOR = 1;
const SENSOR_TYPE_TEMP_HUMIDITY = 2;

const bladeContentSx = {
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  bgcolor: 'var(--surface-page)',
};

const sensorTabsRegionSx = {
  flexShrink: 0,
  bgcolor: 'background.paper',
  borderBottom: '1px solid var(--border-default)',
};

const bladeBodySx = {
  flex: 1,
  minHeight: 0,
  overflow: 'auto',
  bgcolor: 'var(--surface-page)',
};

/** Resolves touched/error state for a dotted field path against one sensor's slice. */
function fieldErrorAt(errors, touched, path) {
  const parts = path.split('.');
  let e = errors;
  let t = touched;
  for (const part of parts) {
    e = e?.[part];
    t = t?.[part];
  }
  return t ? e : undefined;
}

const canEdit = (configuration, field) => configuration.editable_fields?.includes(field) ?? false;

function ColorSensorFields({
  namePrefix,
  sensor,
  errors,
  touched,
  handleChange,
  onCalibrate,
  onShowWatermark,
  commandDisabled = false,
}) {
  const { t } = useTranslation();
  const field = suffix => `${namePrefix}.${suffix}`;
  const errorAt = suffix => fieldErrorAt(errors, touched, suffix);
  const supportsRedThreshold = sensor.red_threshold !== undefined;
  const supportsBlueThreshold = sensor.blue_threshold !== undefined;

  return (
    <Box>
      {onCalibrate || onShowWatermark ? (
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          {onCalibrate ? (
            <Button variant="outlined" size="small" disabled={commandDisabled} onClick={onCalibrate}>
              {t('sensors.blade.calibrate', 'Calibrate')}
            </Button>
          ) : null}
          {onShowWatermark ? (
            <Button variant="outlined" size="small" disabled={commandDisabled} onClick={onShowWatermark}>
              {t('sensors.blade.showWatermark', 'Show Watermark')}
            </Button>
          ) : null}
        </Stack>
      ) : null}
      <Box display="grid" gridTemplateColumns={{ xs: '1fr 1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }} gap={2}>
        <TextField
          label={t('common.name', 'Name')}
          name={field('name')}
          value={sensor.name ?? ''}
          onChange={handleChange}
          disabled={!canEdit(sensor, 'name')}
          error={Boolean(errorAt('name'))}
          helperText={errorAt('name')}
          size="small"
          sx={{ gridColumn: { xs: '1 / -1', md: 'span 2' } }}
        />
        <TextField
          label={t('sensors.blade.locationX', 'X')}
          name={field('location.x')}
          type="number"
          value={sensor.location?.x ?? ''}
          onChange={handleChange}
          disabled={!canEdit(sensor, 'location')}
          error={Boolean(errorAt('location.x'))}
          helperText={errorAt('location.x')}
          size="small"
        />
        <TextField
          label={t('sensors.blade.locationY', 'Y')}
          name={field('location.y')}
          type="number"
          value={sensor.location?.y ?? ''}
          onChange={handleChange}
          disabled={!canEdit(sensor, 'location')}
          error={Boolean(errorAt('location.y'))}
          helperText={errorAt('location.y')}
          size="small"
        />
        <TextField
          label={t('sensors.blade.size', 'Size')}
          name={field('size')}
          type="number"
          value={sensor.size ?? ''}
          onChange={handleChange}
          disabled={!canEdit(sensor, 'size')}
          error={Boolean(errorAt('size'))}
          helperText={errorAt('size')}
          size="small"
        />
        <TextField
          label={t('sensors.blade.threshold', 'Threshold')}
          name={field('threshold')}
          type="number"
          value={sensor.threshold ?? ''}
          onChange={handleChange}
          disabled={!canEdit(sensor, 'threshold')}
          error={Boolean(errorAt('threshold'))}
          helperText={errorAt('threshold')}
          size="small"
        />
        {supportsRedThreshold ? (
          <TextField
            label={t('sensors.blade.redThreshold', 'Red Threshold')}
            name={field('red_threshold')}
            type="number"
            value={sensor.red_threshold ?? ''}
            onChange={handleChange}
            disabled={!canEdit(sensor, 'red_threshold')}
            error={Boolean(errorAt('red_threshold'))}
            helperText={errorAt('red_threshold')}
            size="small"
          />
        ) : null}
        {supportsBlueThreshold ? (
          <TextField
            label={t('sensors.blade.blueThreshold', 'Blue Threshold')}
            name={field('blue_threshold')}
            type="number"
            value={sensor.blue_threshold ?? ''}
            onChange={handleChange}
            disabled={!canEdit(sensor, 'blue_threshold')}
            error={Boolean(errorAt('blue_threshold'))}
            helperText={errorAt('blue_threshold')}
            size="small"
          />
        ) : null}
      </Box>
    </Box>
  );
}

/**
 * Temperature/humidity leaf TextFields, shared by the physical temp/humidity
 * sensor tab and the Common tab's fixed-generation nested bounds. `values`
 * is whichever document (a sensor row or Common) owns the `temperature` /
 * `humidity` objects at `namePrefix`.
 */
function TemperatureHumidityFields({ namePrefix, values, errors, touched, setFieldValue, measurementSystem }) {
  const { t } = useTranslation();
  const field = suffix => `${namePrefix}.${suffix}`;
  const errorAt = suffix => fieldErrorAt(errors, touched, suffix);
  const tempUnit = measurementSystem === 'imperial' ? '°F' : '°C';

  return (
    <>
      <TextField
        label={t('sensors.blade.tempHigh', 'Temp High ({{unit}})', { unit: tempUnit })}
        type="number"
        value={displayTemp(values.temperature?.high, measurementSystem)}
        onChange={e => setFieldValue(field('temperature.high'), inputToRawTemp(e.target.value, measurementSystem))}
        disabled={!canEdit(values, 'temperature')}
        error={Boolean(errorAt('temperature.high'))}
        helperText={errorAt('temperature.high')}
        size="small"
      />
      <TextField
        label={t('sensors.blade.tempLow', 'Temp Low ({{unit}})', { unit: tempUnit })}
        type="number"
        value={displayTemp(values.temperature?.low, measurementSystem)}
        onChange={e => setFieldValue(field('temperature.low'), inputToRawTemp(e.target.value, measurementSystem))}
        disabled={!canEdit(values, 'temperature')}
        error={Boolean(errorAt('temperature.low'))}
        helperText={errorAt('temperature.low')}
        size="small"
      />
      <TextField
        label={t('sensors.blade.tempHysteresis', 'Temp Hysteresis ({{unit}})', { unit: tempUnit })}
        type="number"
        value={displayDeltaTemp(values.temperature?.hysteresis, measurementSystem)}
        onChange={e =>
          setFieldValue(field('temperature.hysteresis'), inputToRawDeltaTemp(e.target.value, measurementSystem))
        }
        disabled={!canEdit(values, 'temperature')}
        error={Boolean(errorAt('temperature.hysteresis'))}
        helperText={errorAt('temperature.hysteresis')}
        size="small"
      />
      <TextField
        label={t('sensors.blade.humidityHigh', 'Humidity High')}
        type="number"
        value={displayRh(values.humidity?.high)}
        onChange={e => setFieldValue(field('humidity.high'), inputToRawRh(e.target.value))}
        disabled={!canEdit(values, 'humidity')}
        error={Boolean(errorAt('humidity.high'))}
        helperText={errorAt('humidity.high')}
        size="small"
      />
      <TextField
        label={t('sensors.blade.humidityLow', 'Humidity Low')}
        type="number"
        value={displayRh(values.humidity?.low)}
        onChange={e => setFieldValue(field('humidity.low'), inputToRawRh(e.target.value))}
        disabled={!canEdit(values, 'humidity')}
        error={Boolean(errorAt('humidity.low'))}
        helperText={errorAt('humidity.low')}
        size="small"
      />
      <TextField
        label={t('sensors.blade.humidityHysteresis', 'Humidity Hysteresis')}
        type="number"
        value={displayRh(values.humidity?.hysteresis)}
        onChange={e => setFieldValue(field('humidity.hysteresis'), inputToRawRh(e.target.value))}
        disabled={!canEdit(values, 'humidity')}
        error={Boolean(errorAt('humidity.hysteresis'))}
        helperText={errorAt('humidity.hysteresis')}
        size="small"
      />
    </>
  );
}

function TempHumSensorFields({ namePrefix, sensor, errors, touched, handleChange, setFieldValue, measurementSystem }) {
  const { t } = useTranslation();
  const field = suffix => `${namePrefix}.${suffix}`;
  const errorAt = suffix => fieldErrorAt(errors, touched, suffix);

  return (
    <Box display="grid" gridTemplateColumns={{ xs: '1fr 1fr', sm: 'repeat(3, 1fr)' }} gap={2}>
      <TextField
        label={t('common.name', 'Name')}
        name={field('name')}
        value={sensor.name ?? ''}
        onChange={handleChange}
        disabled={!canEdit(sensor, 'name')}
        error={Boolean(errorAt('name'))}
        helperText={errorAt('name')}
        size="small"
        sx={{ gridColumn: { xs: '1 / -1', sm: 'span 2' } }}
      />
      <TemperatureHumidityFields
        namePrefix={namePrefix}
        values={sensor}
        errors={errors}
        touched={touched}
        setFieldValue={setFieldValue}
        measurementSystem={measurementSystem}
      />
    </Box>
  );
}

function CommonSensorFields({ common, errors, touched, handleChange, setFieldValue, measurementSystem }) {
  const { t } = useTranslation();
  const errorAt = field => fieldErrorAt(errors, touched, field);
  const fields = [
    ['green_tolerance', t('sensors.blade.greenTolerance', 'Green Tolerance')],
    ['red_tolerance', t('sensors.blade.redTolerance', 'Red Tolerance')],
    ['blue_tolerance', t('sensors.blade.blueTolerance', 'Blue Tolerance')],
    ['watermark_check_time', t('sensors.blade.watermarkCheckTime', 'Watermark Check Time')],
    ['stuck_check_count', t('sensors.blade.stuckCheckCount', 'Stuck Check Count')],
    ['stuck_margin', t('sensors.blade.stuckMargin', 'Stuck Margin')],
  ].filter(([field]) => canEdit(common, field));
  // Fixed-generation Vantage firmware reports temperature/humidity bounds on
  // Common itself rather than a dedicated sensor row.
  const showTemperatureHumidity = canEdit(common, 'temperature') || canEdit(common, 'humidity');

  const scalarFieldsGrid =
    fields.length > 0 ? (
      <Box display="grid" gridTemplateColumns={{ xs: '1fr 1fr', sm: 'repeat(3, 1fr)' }} gap={2}>
        {fields.map(([field, label]) => (
          <TextField
            key={field}
            label={label}
            name={`common.${field}`}
            type="number"
            value={common[field] ?? ''}
            onChange={handleChange}
            error={Boolean(errorAt(field))}
            helperText={errorAt(field)}
            size="small"
          />
        ))}
      </Box>
    ) : null;

  if (fields.length === 0 && !showTemperatureHumidity) {
    return (
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {t('sensors.blade.commonUnavailable', 'Common sensor configuration has not been reported by this unit.')}
      </Typography>
    );
  }

  if (!showTemperatureHumidity) {
    return scalarFieldsGrid;
  }

  return (
    <Stack spacing={3}>
      {scalarFieldsGrid}
      <Box display="grid" gridTemplateColumns={{ xs: '1fr 1fr', sm: 'repeat(3, 1fr)' }} gap={2}>
        <TemperatureHumidityFields
          namePrefix="common"
          values={common}
          errors={errors}
          touched={touched}
          setFieldValue={setFieldValue}
          measurementSystem={measurementSystem}
        />
      </Box>
    </Stack>
  );
}

function UnknownSensorFields({ namePrefix, sensor, errors, touched, handleChange }) {
  const { t } = useTranslation();
  const nameError = fieldErrorAt(errors, touched, 'name');

  return (
    <Stack spacing={2}>
      <TextField
        label={t('common.name', 'Name')}
        name={`${namePrefix}.name`}
        value={sensor.name ?? ''}
        onChange={handleChange}
        disabled={!canEdit(sensor, 'name')}
        error={Boolean(nameError)}
        helperText={nameError}
        size="small"
      />
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {t('sensors.blade.unknownSensorType', 'Additional configuration for this sensor is managed by the device.')}
      </Typography>
    </Stack>
  );
}

function SensorBladeSkeleton() {
  return (
    <>
      <Box sx={sensorTabsRegionSx}>
        <Stack direction="row" spacing={1} sx={{ px: 'var(--page-gap)', py: 'var(--space-1)' }}>
          {[96, 96, 96].map((width, index) => (
            <Skeleton
              key={`sensor-tab-skeleton-${index}`}
              variant="rectangular"
              width={width}
              height={32}
              sx={{ borderRadius: 'var(--radius-md)' }}
            />
          ))}
        </Stack>
      </Box>
      <Box sx={{ ...bladeBodySx, p: 'var(--space-3)' }}>
        <Stack spacing={2}>
          {[0, 1, 2, 3].map(index => (
            <Skeleton key={`sensor-field-skeleton-${index}`} variant="rounded" height={40} />
          ))}
        </Stack>
      </Box>
    </>
  );
}

/**
 * @param {object}          props
 * @param {boolean}         props.open
 * @param {Function}        props.onClose
 * @param {number|string}   props.siteId
 * @param {object|null}     props.sensorConfiguration
 * @param {boolean}         [props.loading=false]
 * @param {boolean}         [props.canSendCommands=false]
 */
export default function SensorBlade({
  open,
  onClose,
  siteId,
  sensorConfiguration,
  loading: dashboardLoading = false,
  canSendCommands = false,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { commandLocked, runWrite } = useSiteCommandWrite(siteId);
  const user = useSelector(getAuthUserSelector);
  const measurementSystem = getUserMeasurementSystem(user);

  const [configSnapshot, setConfigSnapshot] = useState(null);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [commandSubmitting, setCommandSubmitting] = useState(false);
  const [nonFieldMessages, setNonFieldMessages] = useState([]);

  const config = configSnapshot?.siteId === siteId ? configSnapshot.configuration : null;
  const hasSupportedIncomingConfig = sensorConfiguration?.schema_version === SENSOR_CONFIG_SCHEMA_VERSION;
  const loading = open && config == null && (dashboardLoading || hasSupportedIncomingConfig);
  const loadError = open && config == null && !loading;

  useEffect(() => {
    if (!open) {
      setConfigSnapshot(null);
      setNonFieldMessages([]);
      setActiveTabIndex(0);
      return undefined;
    }
    if (config == null && hasSupportedIncomingConfig) {
      setConfigSnapshot({ siteId, configuration: sensorConfiguration });
    }
    return undefined;
  }, [config, hasSupportedIncomingConfig, open, sensorConfiguration, siteId]);

  useEffect(() => {
    if (open) {
      setActiveTabIndex(0);
      setNonFieldMessages([]);
    }
  }, [open, siteId]);

  const initialValues = config
    ? {
        schema_version: config.schema_version,
        common: buildCommonInitialValue(config.common),
        sensors: buildSensorInitialValues(config.sensors),
      }
    : { schema_version: SENSOR_CONFIG_SCHEMA_VERSION, common: buildCommonInitialValue(null), sensors: [] };

  const validationMessages = {
    required: t('validation.required', 'Required'),
    wholeNumber: t('sensorConfig.mustBeWholeNumber', 'Must be a whole number'),
    min: value => t('sensorConfig.mustBeAtLeast', 'Must be at least {{value}}', { value }),
    max: value => t('sensorConfig.mustBeAtMost', 'Must be at most {{value}}', { value }),
    maxCharacters: value => t('sensorConfig.maxCharacters', 'Max {{value}} characters', { value }),
  };
  const validationSchema = buildSensorValidationSchema(validationMessages);

  const commandFor = (action, sensorNumber) => async () => {
    if (commandLocked || commandSubmitting) return;

    setCommandSubmitting(true);
    try {
      await runWrite(() => dispatchSensorOperation(siteId, action, sensorNumber), { requiresCommand: true });
    } catch {
      dispatch(
        addToast({
          message: t('commandCatalog.sendError', 'Failed to send command.'),
          level: 'error',
        }),
      );
    } finally {
      setCommandSubmitting(false);
    }
  };

  const handleSubmit = async (values, { setErrors }) => {
    setIsSaving(true);
    setNonFieldMessages([]);
    const payload = generateSensorConfigPatch(initialValues, values);
    const requiresCommand = sensorConfigRequiresCommandLease(payload);

    try {
      const outcome = await runWrite(() => patchSiteSensorConfig(siteId, payload), { requiresCommand });
      if (!outcome.executed) return;
      if (outcome.value?.schema_version !== SENSOR_CONFIG_SCHEMA_VERSION) {
        throw new Error('Unsupported sensor configuration schema version');
      }
      dispatch(addToast({ message: t('sensors.configurationUpdated', 'Configuration updated'), level: 'success' }));
      onClose();
    } catch (error) {
      setNonFieldMessages(
        applyApiErrorToFormik(error, {
          setErrors: fieldErrors => {
            const nestedErrors = Object.entries(fieldErrors).reduce((result, [path, message]) => {
              const formPath = mapSensorPatchErrorPath(path, payload, values);
              return setIn(result, formPath, message);
            }, {});
            setErrors(nestedErrors);
          },
          knownFields: buildSensorPatchKnownFields(payload),
          genericMessage: t('sensors.configurationUpdateFailed', 'Configuration update failed'),
        }),
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values, isValid, dirty, submitForm, setFieldValue, handleChange, errors, touched }) => {
        const supportsCalibration = canSendCommands && Boolean(config?.available_actions?.includes('calibrate_all'));
        const pendingPatch = generateSensorConfigPatch(initialValues, values);
        const saveRequiresCommand = sensorConfigRequiresCommandLease(pendingPatch);
        const headerAction = (
          <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
            {supportsCalibration ? (
              <Tooltip title={t('sites.vantageSensorCalibrateAllTooltip')}>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={commandLocked || commandSubmitting}
                  onClick={commandFor('calibrate_all')}
                >
                  {t('sensors.blade.calibrateAll', 'Calibrate All')}
                </Button>
              </Tooltip>
            ) : null}
            <Button
              variant="contained"
              size="small"
              disabled={!dirty || !isValid || isSaving || loading || (commandLocked && saveRequiresCommand)}
              onClick={submitForm}
            >
              {t('actions.save', 'Save')}
            </Button>
          </Stack>
        );

        const tabs = buildSensorBladeTabs(values.sensors);
        const activeIndex = Math.min(activeTabIndex, Math.max(tabs.length - 1, 0));
        const activeTab = tabs[activeIndex];
        const activeSensorIndex = activeTab?.kind === 'sensor' ? activeTab.sensorIndex : null;
        const activeSensor = activeSensorIndex == null ? null : values.sensors[activeSensorIndex];
        const navigationTabs = tabs.map((tab, index) => {
          const sensor = tab.kind === 'sensor' ? values.sensors[tab.sensorIndex] : null;
          const primary =
            tab.kind === 'common'
              ? t('sensors.blade.commonTab', 'Common')
              : sensor.name ||
                `${t('sensors.sensor', 'Sensor')} ${Number(sensor.sensor_number ?? tab.sensorIndex) + 1}`;
          const secondary =
            tab.kind === 'common'
              ? t('sensors.blade.unitSettings', 'Unit settings')
              : getSensorTypeName(sensor.sensor_type, t);

          return { key: tab.key, value: index, primary, secondary };
        });

        return (
          <Form>
            <DashboardBlade
              open={open}
              onClose={onClose}
              width={BLADE_WIDTH}
              title={t('deviceConfig.sensorsTitle', 'Sensors')}
              headerAction={headerAction}
            >
              <Box sx={bladeContentSx}>
                <FormErrorAlert messages={nonFieldMessages} sx={{ mx: 'var(--page-gap)', mt: 'var(--space-3)' }} />
                {loading ? (
                  <SensorBladeSkeleton />
                ) : loadError ? (
                  <Box sx={{ ...bladeBodySx, p: 'var(--space-3)' }}>
                    <Typography color="error">
                      {t('deviceConfig.loadError', 'Could not load device configuration.')}
                    </Typography>
                  </Box>
                ) : tabs.length === 0 ? (
                  <Box sx={{ ...bladeBodySx, p: 'var(--space-3)' }}>
                    <Typography>{t('sensors.blade.noSensors', 'No sensors found.')}</Typography>
                  </Box>
                ) : (
                  <>
                    <ConfigurationBladeTabs
                      tabs={navigationTabs}
                      value={activeIndex}
                      onChange={(_event, value) => setActiveTabIndex(value)}
                      ariaLabel={t('deviceConfig.sensorsTitle', 'Sensors')}
                    />
                    <Box sx={{ ...bladeBodySx, p: 'var(--space-3)' }}>
                      {activeTab.kind === 'common' ? (
                        <CommonSensorFields
                          common={values.common}
                          errors={errors.common}
                          touched={touched.common}
                          handleChange={handleChange}
                          setFieldValue={setFieldValue}
                          measurementSystem={measurementSystem}
                        />
                      ) : activeSensor.sensor_type === SENSOR_TYPE_COLOR ? (
                        <ColorSensorFields
                          namePrefix={`sensors[${activeSensorIndex}]`}
                          sensor={activeSensor}
                          errors={errors.sensors?.[activeSensorIndex]}
                          touched={touched.sensors?.[activeSensorIndex]}
                          handleChange={handleChange}
                          onCalibrate={
                            supportsCalibration ? commandFor('calibrate', activeSensor.sensor_number) : undefined
                          }
                          onShowWatermark={
                            supportsCalibration ? commandFor('show_watermark', activeSensor.sensor_number) : undefined
                          }
                          commandDisabled={commandLocked || commandSubmitting}
                        />
                      ) : activeSensor.sensor_type === SENSOR_TYPE_TEMP_HUMIDITY ? (
                        <TempHumSensorFields
                          namePrefix={`sensors[${activeSensorIndex}]`}
                          sensor={activeSensor}
                          errors={errors.sensors?.[activeSensorIndex]}
                          touched={touched.sensors?.[activeSensorIndex]}
                          handleChange={handleChange}
                          setFieldValue={setFieldValue}
                          measurementSystem={measurementSystem}
                        />
                      ) : (
                        <UnknownSensorFields
                          namePrefix={`sensors[${activeSensorIndex}]`}
                          sensor={activeSensor}
                          errors={errors.sensors?.[activeSensorIndex]}
                          touched={touched.sensors?.[activeSensorIndex]}
                          handleChange={handleChange}
                        />
                      )}
                    </Box>
                  </>
                )}
              </Box>
            </DashboardBlade>
          </Form>
        );
      }}
    </Formik>
  );
}

SensorBlade.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  siteId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  sensorConfiguration: PropTypes.object,
  loading: PropTypes.bool,
  canSendCommands: PropTypes.bool,
};
